import { Directive, Injector, Input, OnInit } from '@angular/core';
import { Change, ChangeType, FrameContext, Modification, ModifyType, UIStateObservableParam, VariableParseService } from '@farris/devkit';
import { PortletComponent } from '@gspwidget/portlet';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ExpressionType, IFilterDependency } from './types';

const DATA_PATTERN = /\{:?DATA~(\S+?)\}$/;
const DATA_PATTERN_GLOBAL = new RegExp(DATA_PATTERN, 'g');
const UI_STATE_PATTERN = /\{:?UISTATE~(\S+?)\}$/;
const UI_STATE_PATTERN_GLOBAL = new RegExp(UI_STATE_PATTERN, 'g');

@Directive({
  selector: '[portlet-filter-binding]'
})
export class PortletFilterBindingDirective implements OnInit {
  private subject: Subject<any> = new Subject<any>();
  private readonly FilterFieldKey = 'dpId';
  private readonly FilterValueKey = 'value';
  private values: Map<string, any> = new Map<string, any>();

  private get changes(): Observable<any> {
    return this.subject;
  }
  @Input('portlet-filter-binding') filters: string | any[];

  constructor(private injector: Injector, private portletComponent: PortletComponent, private frameContext: FrameContext, private variableParseService: VariableParseService) { }
  public ngOnInit(): void {
    // çå¬æ°æ®ååï¼æ§è¡ç¬¬ä¸æ¬¡çç»å®
    this.frameContext.bindingData.changes.pipe(filter((change: Change) => {
      return (change.type === ChangeType.Load || change.type === ChangeType.Remove || change.type === ChangeType.Update || change.type === ChangeType.SelectionChanged) && (!change.path || change.path.length < 1);
    })).subscribe((change: Change) => {
      this.filter();
    });
    this.changes.pipe(
      filter((change: { value: any, type: ExpressionType, path: string; }) => {
        const prefix = change.type === ExpressionType.BindingData ? 'DATA' : 'UISTATE';
        const currentId = this.frameContext.bindingData.list.currentId;
        const storeKeys = [prefix];
        if (change.type === ExpressionType.BindingData && currentId) {
          storeKeys.push(currentId);
        }
        storeKeys.push(change.path);
        const valueKey = storeKeys.join('/');
        const currentValue = this.values.get(valueKey) || null;
        if (change.value !== currentValue) {
          this.values.set(valueKey, change.value);
          return true;
        }
        return false;
      })
    ).subscribe((change: { frameContext: FrameContext, value: any, type: ExpressionType, path: string; }) => {
      this.filter(true);
    });
    this.observeChanges();
  }
  private filter(isValueChange: boolean = false) {
    const filters = this.buildFilters(isValueChange);
    this.portletComponent.widget.handleFilterChange(filters);
  }
  public deserializeFilter() {
    if (!this.filters) {
      return [];
    }
    let filters = [];
    if (typeof this.filters === 'string') {
      filters = JSON.parse(this.filters);
    } else if (Array.isArray(this.filters)) {
      filters = this.filters;
    } else {
      console.error('invalid filters');
    }
    return filters;
  }
  /**
   * å°è¡¨åéç½®çè¿æ»¤æ¡ä»¶è½¬æ¢ä¸ºå°é¨ä»¶æ¯æçè¿æ»¤æ¡ä»¶
   * @param isValueChange
   * @returns
   */
  private buildFilters(isValueChange: boolean): any[] {
    const filters = this.deserializeFilter();
    const result = [];
    if (!filters || filters.length < 1) {
      return result;
    }
    filters.forEach((filter) => {
      const value = this.parseValue(filter[this.FilterValueKey], isValueChange);
      result.push({
        dpId: filter[this.FilterFieldKey],
        value: value
      });
    });
    return result;
  }
  private parseValue(expression: string, isValueChange: boolean) {
    if (expression.match(DATA_PATTERN_GLOBAL) && isValueChange) {
      const pathMatches = expression.match(DATA_PATTERN);
      const dependency = this.buildDependency(pathMatches, ExpressionType.BindingData);
      if (dependency) {
        const { frameId = null } = dependency;
        const paths = dependency.paths;
        const currentId = this.frameContext.bindingData.list.currentId;
        const key = `DATA/${currentId}/${paths.join('/')}`;
        if (this.values.has(key)) {
          const cacheValue = this.values.get(key);
          if (frameId) {
            const targetFrameContext = this.getFrameContext(frameId);
            const bindingValue = this.variableParseService.parse(expression, targetFrameContext);
            if (cacheValue !== bindingValue) {
              this.values.set(key, bindingValue);
              return bindingValue;
            }
          }
          return cacheValue;
        }
      }
    }
    return this.variableParseService.parse(expression, this.frameContext);
  }
  private collectDependencies(expression: string): IFilterDependency[] {
    const bindingDataDependencies = this.getBindingDataDependencies(expression);
    const uistateDependencies = this.getUIstateDependencies(expression);
    return bindingDataDependencies.concat(uistateDependencies);
  }
  private getBindingDataDependencies(expression: string): IFilterDependency[] {
    const matchs = expression.match(DATA_PATTERN_GLOBAL);
    return this.buildDependencies(matchs, DATA_PATTERN, ExpressionType.BindingData);
  }
  private getUIstateDependencies(expression: string): IFilterDependency[] {
    const matchs = expression.match(UI_STATE_PATTERN_GLOBAL);
    return this.buildDependencies(matchs, UI_STATE_PATTERN, ExpressionType.UIState);
  }
  private buildDependencies(expressions: RegExpMatchArray, expr: RegExp, expressionType: ExpressionType) {
    const dependencies: IFilterDependency[] = [];
    expressions && expressions.forEach((expression: string) => {
      const pathMatches = expression.match(expr);
      const dependency = this.buildDependency(pathMatches, expressionType);
      dependencies.push(dependency);
    });
    return dependencies;
  }
  private buildDependency(matchs: RegExpMatchArray, expressionType: ExpressionType) {
    if (matchs && matchs.length === 2) {
      const exps = matchs[1].split('/').filter((p) => p);
      let frameId = exps.shift();
      const paths = exps;
      if (frameId.startsWith('#{') && frameId.endsWith('}')) {
        frameId = frameId.substring(2, frameId.length - 1);
      }
      const dependency: IFilterDependency = {
        type: expressionType,
        frameId,
        paths
      };
      return dependency;
    }
  }
  private getFrameContext(frameId: string) {
    frameId = this.frameContext.getFrameId(frameId);
    return this.frameContext.appContext.frameContextManager.getFrameContextById(frameId);
  }
  private observeChanges() {
    const filters = this.deserializeFilter();
    filters && filters.forEach((filter: any) => {
      const dependencies = this.collectDependencies(filter[this.FilterValueKey]);
      if (dependencies && dependencies.length > 0) {
        dependencies.forEach((dep: IFilterDependency) => {
          const frameContext = this.getFrameContext(dep.frameId);
          if (dep.type === ExpressionType.BindingData) {
            this.observeBindingDataChange(frameContext, dep.paths);
          } else if (dep.type === ExpressionType.UIState) {
            this.observeUIStateChange(frameContext, dep.paths.pop());
          }
        });
      }
    });
  }
  private observeUIStateChange(frameContext: FrameContext, path: string) {
    frameContext.uiState.changes.subscribe((change: UIStateObservableParam) => {
      if (change.field === path) {
        this.subject.next({ value: change.value, type: ExpressionType.UIState, path });
      }
    });
  }
  private observeBindingDataChange(frameContext: FrameContext, paths: string[]) {
    // çå¬çé¢åæ´
    frameContext.bindingData.changes.pipe(
      // åªçå¬ä¸»è¡¨åå
      filter((change: Change) => change.type === ChangeType.ValueChanged)
    ).subscribe((change: Change) => {
      if (change.path.join('/') === paths.join('/')) {
        const path = paths.join('/');
        this.subject.next({ value: change.value, type: ExpressionType.BindingData, path, frameContext });
      }
    });

    frameContext.repository.entityCollection.changes.pipe(
      filter((modify: Modification) => {
        if (modify.type !== ModifyType.ValueChange) {
          return false;
        }
        const path = modify.path;
        const fieldPaths = [];
        path.forEach((value: string, index: number) => {
          if (index % 2 === 0) {
            return;
          }
          fieldPaths.push(value);
        });
        return fieldPaths.join('/') === paths.join('/');
      })
    ).subscribe((modify) => {
      const path = paths.join('/');
      this.subject.next({ value: modify.value, type: ExpressionType.BindingData, path, frameContext });
    });
  }
}
